import { useMapEvents } from "react-leaflet";
import { useCallback } from "react";
import { LeafletMouseEvent } from "leaflet";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import {
  LIGHTNING_PERIODICITY_DEFAULT,
  LIGHTNING_RADIUS_DEFAULT_KM,
  LIGHTNING_RECURRENCE_HOURS_DEFAULT,
  LIGHTNING_SILENCE_WINDOW_DEFAULT_MIN,
  periodicityToNotificationWindowMin,
} from "components/SensorAlertSection/lightning/lightning-constants";

/**
 * Handler for map clicks in Sensor Selection Mode.
 *
 * - On the "rain" tab: absorbs clicks so the default MapClickHandler doesn't
 *   open meteograms / change stations. Sensor toggling is handled per-marker.
 * - On the "lightning" tab (spec 043): places / replaces the lightning point
 *   on `insightAlertStore.lightningSelection`. Defaults are applied on the
 *   first click (radius 10 km, silence window 5 min, recurring 1h periodicity).
 */
const SensorSelectionMapClickHandlerBase = () => {
  const {
    dataStores: { forecastChartPageStore, insightAlertStore },
  } = useStores();

  useMapEvents({
    click: useCallback(
      (e: LeafletMouseEvent) => {
        if (forecastChartPageStore.sensorSelectionTab !== "lightning") return;

        const { lat, lng } = e.latlng;
        const current = insightAlertStore.lightningSelection;
        if (current) {
          insightAlertStore.setLightningSelection({
            ...current,
            point: { lat, lng },
          });
        } else {
          insightAlertStore.setLightningSelection({
            point: { lat, lng },
            radiusKm: LIGHTNING_RADIUS_DEFAULT_KM,
            allClearMin: LIGHTNING_SILENCE_WINDOW_DEFAULT_MIN,
            notificationWindowMin: periodicityToNotificationWindowMin(
              LIGHTNING_PERIODICITY_DEFAULT,
              LIGHTNING_RECURRENCE_HOURS_DEFAULT,
            ),
          });
        }
      },
      [forecastChartPageStore, insightAlertStore],
    ),
  });

  return null;
};

export const SensorSelectionMapClickHandler = observer(
  SensorSelectionMapClickHandlerBase,
);
